import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { usersService } from '../services/usersService'
import { ACCOUNT_STATUS_LABELS, formatSuspensionReason } from '../services/subscriptionsService'
import { useDataFetch } from '../hooks/useDataFetch'
import { formatDateTime } from '../utils/helpers'

export default function UserProfile() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { data, loading, error, refetch } = useDataFetch(() => usersService.getById(id), [id])
    const [accountStatus, setAccountStatus] = useState('active')
    const [subscriptionState, setSubscriptionState] = useState('trial')
    const [saving, setSaving] = useState(false)

    const user = data?.user
    const organisation = data?.organisation
    const providerProfile = data?.providerProfile
    const teamMembers = data?.teamMembers || []

    const { data: history, loading: historyLoading } = useDataFetch(
        () => (user?.tenantId ? usersService.getAccountHistory(user.tenantId) : Promise.resolve([])),
        [user?.tenantId]
    )

    useEffect(() => {
        if (!user) return
        setAccountStatus(user.accountStatus)
        setSubscriptionState(user.subscriptionState)
    }, [user])

    const profileStats = useMemo(() => {
        if (!providerProfile) return []
        return [
            { label: 'Service Types', value: toCount(providerProfile.service_types) },
            { label: 'Regions', value: toCount(providerProfile.regions) },
            { label: 'Keywords', value: toCount(providerProfile.keywords) },
            { label: 'Value Bands', value: toCount(providerProfile.value_bands) },
        ]
    }, [providerProfile])

    const handleSaveAccess = async () => {
        if (!user) return
        try {
            setSaving(true)
            await usersService.updateDetails(user.id, { accountStatus, subscriptionState })
            await refetch()
        } catch (err) {
            console.error(err)
            alert('Failed to update user profile')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="card-admin p-12 text-center text-admin-muted font-bold">
                Loading user profile...
            </div>
        )
    }

    if (error || !user) {
        return (
            <div className="space-y-4">
                <button onClick={() => navigate('/users')} className="btn-secondary btn-sm">
                    Back to Users
                </button>
                <div className="p-4 bg-status-error-bg border border-status-error/20 text-status-error text-sm font-bold rounded-lg">
                    {error || 'User profile not found.'}
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-start gap-4">
                    <Link to="/users" className="btn-secondary btn-sm mt-1">
                        Back
                    </Link>
                    <div>
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 className="text-2xl font-bold text-admin-heading tracking-tight">{user.username}</h1>
                            <AccountStatusBadge status={user.accountStatus} />
                            {user.isAdmin && <span className="badge badge-info">Admin</span>}
                        </div>
                        <p className="text-sm text-admin-muted font-medium mt-1">{user.email}</p>
                    </div>
                </div>
                <button onClick={refetch} className="btn-secondary shadow-sm">
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <MetricCard label="Subscription" value={user.subscriptionState} />
                <MetricCard label="Email Verified" value={user.emailVerified ? 'Yes' : 'No'} highlight={user.emailVerified} />
                <MetricCard label="Created" value={formatDateTime(user.createdAt)} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 space-y-6">
                    <section className="card-admin">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">User Details</h2>
                        </div>
                        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InfoField label="Username" value={user.username} />
                            <InfoField label="Email" value={user.email} />
                            <InfoField label="Designation / Job Title" value={user.designation} />
                            <InfoField label="User ID" value={user.id} mono />
                            <InfoField label="Tenant ID" value={user.tenantId || 'No tenant'} mono />
                            <InfoField label="Last Updated" value={formatDateTime(user.updatedAt)} />
                            <InfoField label="First Login Pending" value={user.isFirstTime ? 'Yes' : 'No'} />
                        </div>
                    </section>

                    <section className="card-admin">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">Organisation</h2>
                        </div>
                        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InfoField label="Name" value={organisation?.name || user.organisation} />
                            <InfoField label="Account Status" value={ACCOUNT_STATUS_LABELS[organisation?.account_status] || organisation?.account_status} />
                            {organisation?.suspension_reason && (
                                <InfoField label="Suspension Reason" value={formatSuspensionReason(organisation.suspension_reason, organisation.suspension_reason_other)} />
                            )}
                            <InfoField label="Raw Subscription Status" value={organisation?.subscription_status || user.subscriptionState} />
                            <InfoField label="Referral Code" value={organisation?.referral_code || 'None'} mono />
                            <InfoField label="Stripe Customer" value={organisation?.stripe_customer_id || 'None'} mono />
                            <InfoField label="Trial End" value={formatDateTime(organisation?.trial_end)} />
                            <InfoField label="Locations" value={organisation?.location_quantity ?? 'Not set'} />
                        </div>
                    </section>

                    <section className="card-admin">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">Provider Profile</h2>
                        </div>
                        {providerProfile ? (
                            <div className="card-body space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {profileStats.map((stat) => (
                                        <div key={stat.label} className="bg-gray-50 border border-admin-border rounded-lg p-3">
                                            <p className="text-[10px] font-bold text-admin-muted uppercase tracking-wide">{stat.label}</p>
                                            <p className="text-xl font-bold text-brand-navy mt-1">{stat.value}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InfoField label="Service Types" value={toList(providerProfile.service_types)} />
                                    <InfoField label="Nations" value={toList(providerProfile.nations)} />
                                    <InfoField label="Regions" value={toList(providerProfile.regions)} />
                                    <InfoField label="Keywords" value={toList(providerProfile.keywords)} />
                                    <InfoField label="Value Min" value={providerProfile.value_min || 'Not set'} />
                                    <InfoField label="Value Max" value={providerProfile.value_max || 'Not set'} />
                                    <InfoField label="CDP Registered" value={providerProfile.cdp_registered ? 'Yes' : 'No'} />
                                    <InfoField label="Growth Focus" value={providerProfile.growth_focus || 'Not set'} />
                                </div>
                            </div>
                        ) : (
                            <div className="p-8 text-center text-admin-muted font-bold">
                                No provider profile has been created for this organisation.
                            </div>
                        )}
                    </section>

                    <section className="card-admin">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">Account History</h2>
                            <span className="badge badge-neutral">{history?.length || 0}</span>
                        </div>
                        {historyLoading ? (
                            <div className="p-6 text-center text-admin-muted font-bold">Loading history...</div>
                        ) : !history?.length ? (
                            <div className="p-6 text-center text-admin-muted font-bold">No account history recorded yet.</div>
                        ) : (
                            <div className="divide-y divide-admin-border max-h-96 overflow-y-auto">
                                {history.map((event) => (
                                    <div key={event.id} className="p-4 flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-admin-text">
                                                {event.event_type.replace(/_/g, ' ')}
                                            </p>
                                            <p className="text-xs text-admin-muted mt-0.5">
                                                {event.account_status_before || '—'} → {event.account_status_after || '—'}
                                            </p>
                                            {event.suspension_reason && (
                                                <p className="text-xs text-admin-muted mt-0.5">Reason: {formatSuspensionReason(event.suspension_reason, event.suspension_reason_other)}</p>
                                            )}
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <span className="badge badge-neutral text-[10px]">{event.initiated_by}</span>
                                            <p className="text-[10px] text-admin-muted mt-1 font-mono">{formatDateTime(event.created_at)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

                <div className="space-y-6">
                    <section className="card-admin">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">Access Control</h2>
                        </div>
                        <div className="card-body space-y-4">
                            <div>
                                <label className="label-admin">Account Status</label>
                                <select
                                    value={accountStatus}
                                    onChange={(e) => setAccountStatus(e.target.value)}
                                    className="select-admin font-bold"
                                >
                                    <option value="active">Active</option>
                                    <option value="suspended">Suspended</option>
                                    <option value="read-only">Read-only</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-admin">Subscription State</label>
                                <select
                                    value={subscriptionState}
                                    onChange={(e) => setSubscriptionState(e.target.value)}
                                    className="select-admin font-bold"
                                >
                                    <option value="trial">Trial</option>
                                    <option value="active">Active</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="expired">Expired</option>
                                </select>
                            </div>
                            <button
                                onClick={handleSaveAccess}
                                disabled={saving}
                                className="btn-primary w-full"
                            >
                                {saving ? 'Saving...' : 'Save Access'}
                            </button>
                        </div>
                    </section>

                    <section className="card-admin">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">Team Members</h2>
                            <span className="badge badge-neutral">{teamMembers.length}</span>
                        </div>
                        <div className="divide-y divide-admin-border">
                            {teamMembers.length === 0 ? (
                                <div className="p-6 text-center text-admin-muted font-bold">No team members found.</div>
                            ) : (
                                teamMembers.map((member) => (
                                    <div key={member.id} className="p-4 flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <Link
                                                to={`/user-profile/${member.id}`}
                                                className="text-sm font-bold text-brand-navy hover:underline truncate block"
                                            >
                                                {member.name}
                                            </Link>
                                            <p className="text-xs text-admin-muted truncate">{member.email}</p>
                                        </div>
                                        <span className={`badge ${member.is_active ? 'badge-success' : 'badge-error'}`}>
                                            {member.is_active ? 'Active' : 'Suspended'}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    )
}

function MetricCard({ label, value, highlight }) {
    return (
        <div className="card-admin p-5">
            <p className="text-xs font-bold text-admin-muted uppercase tracking-wider">{label}</p>
            <p className={`text-xl font-bold mt-2 tracking-tight capitalize ${highlight ? 'text-status-success' : 'text-brand-navy'}`}>{value || 'Not set'}</p>
        </div>
    )
}

function InfoField({ label, value, mono = false }) {
    return (
        <div className="bg-gray-50 border border-admin-border rounded-lg p-3 min-w-0">
            <p className="text-[10px] font-bold text-admin-muted uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-sm font-bold text-admin-text break-words ${mono ? 'font-mono text-xs' : ''}`}>
                {value || 'Not set'}
            </p>
        </div>
    )
}

function AccountStatusBadge({ status }) {
    const styles = {
        active: 'badge-success',
        suspended: 'badge-error',
        'read-only': 'badge-neutral',
    }

    return (
        <span className={`badge ${styles[status] || 'badge-neutral'}`}>
            {status}
        </span>
    )
}

function toArray(value) {
    if (Array.isArray(value)) return value
    if (!value) return []
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : []
        } catch {
            return value ? [value] : []
        }
    }
    return []
}

function toCount(value) {
    return toArray(value).length
}

function toList(value) {
    const list = toArray(value)
    return list.length ? list.join(', ') : 'Not set'
}
